import { Component, OnInit } from '@angular/core';
import { ProfanityFilterService } from './profanity-filter.service';
import { ToastrService } from 'ngx-toastr';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-features-profanity-filter',
  templateUrl: './features-profanity-filter.component.html',
  styleUrls: ['./features-profanity-filter.component.scss']
})
export class FeaturesProfanityFilterComponent implements OnInit {

  profanityWords: any = [];
  wordsToRestrict = '';
  wordsToAllow = '';
  isLoading = false;

  constructor(
    private profanityService: ProfanityFilterService,
    private toastr: ToastrService,
    private popUpDialog: PopUpDialogService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.loadProfanityWords();
  }
  
  async onAddButtonClick(): Promise<void> {
    // Validate input first
    if (!this.hasValidInput()) {
      this.toastr.error('Please enter at least one word to add or remove', 'No Input Detected');
      return;
    }

    // Check for duplicate words
    const duplicates = this.profanityService.findDuplicateWords(
      this.wordsToRestrict, 
      this.wordsToAllow
    );

    if (duplicates.length > 0) {
      // Show toastr error for duplicates
      const duplicateList = duplicates.join(', ');
      this.toastr.error(
        `Duplicate words found: "${duplicateList}". Please remove duplicates and keep each word in only one input.`, 
        'Duplicate Words Detected',
        { timeOut: 5000 }
      );
    } else {
      // Show main confirmation dialog
      this.popUpDialog.openSaveActionDialog(
        this.getConfirmationMessage(),
        () => this.processWordsConfirmed(),
        () => console.log('Operation cancelled')
      );
    }
  }
  
  loadProfanityWords(): void {
    this.isLoading = true;
    this.spinner.show().catch((err) => {console.log(err)});;
    
    this.profanityService.getProfanityFilterWords().subscribe({
      next: (response) => {
        this.profanityWords = response.result || [];
        this.isLoading = false;
        this.spinner.hide().catch((err) => {console.log(err)});
      },
      error: (error) => {
        this.toastr.error('Failed to load profanity words', 'Error');
        this.isLoading = false;
        this.spinner.hide().catch((err) => {console.log(err)});
      }
    });
  }

  // Generate detailed confirmation message
  private getConfirmationMessage(): string {
    const restrictWords = this.profanityService.parseWordsFromInput(this.wordsToRestrict);
    const allowWords = this.profanityService.parseWordsFromInput(this.wordsToAllow);

    const messages: string[] = [];

    if (restrictWords.length > 0) {
      messages.push(`Restrict ${restrictWords.length} words`);
    }
    
    if (allowWords.length > 0) {
      messages.push(`Allow ${allowWords.length} words`);
    }

    if (messages.length === 2) {
      return `Do you want to ${messages[0].toLowerCase()} and ${messages[1].toLowerCase()}?`;
    } else if (messages.length === 1) {
      return `Do you want to ${messages[0].toLowerCase()}?`;
    }
    
    return 'Do you want to proceed?';
  }

  // Validate if there's any input
  private hasValidInput(): boolean {
    return this.wordsToRestrict.trim().length > 0 || this.wordsToAllow.trim().length > 0;
  }

  // Execute the actual processing after confirmation
  private async processWordsConfirmed(): Promise<void> {
    this.isLoading = true;
    this.spinner.show().catch((err) => {console.log(err)});;

    try {
      const result = await this.profanityService.processWords(
        this.wordsToRestrict,
        this.wordsToAllow
      );

      if (result.success) {
        // Show success messages
        this.toastr.success(
          `Successfully saved the profanity words changes`, 
          'Success'
        );

        // Clear inputs and reload
        this.wordsToRestrict = '';
        this.wordsToAllow = '';
        this.loadProfanityWords();
      } else {
        // Show error messages
        result.errors.forEach(error => {
          this.toastr.error(error, 'Error');
        });
        this.isLoading = false;
        this.spinner.hide().catch((err) => {console.log(err)});;
      }
    } catch (error) {
      this.toastr.error('An unexpected error occurred', 'Error');
      this.isLoading = false;
      this.spinner.hide().catch((err) => {console.log(err)});;
    }
  }
}
